import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import {
  Home, TreePine, Utensils, ChefHat, Sparkles, CalendarDays,
  Gift, HandHeart, User, Shield, LogOut, Menu, X, Users, Wallet, Globe,
} from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { setMoneyLocale, LANG_MARKET } from "../lib/format";
import { api } from "../lib/api";
import LanguageSelector from "./LanguageSelector";
import SnowLayer from "./SnowLayer";
import AmbientMusic from "./AmbientMusic";
import TutorialModal from "./TutorialModal";

const NAV = [
  { to: "/app", key: "nav.home", icon: Home, end: true },
  { to: "/app/create", key: "nav.create", icon: Sparkles },
  { to: "/app/decoration", key: "nav.decoration", icon: TreePine },
  { to: "/app/ceia", key: "nav.ceia", icon: Utensils },
  { to: "/app/recipes", key: "nav.recipes", icon: ChefHat },
  { to: "/app/table", key: "nav.table", icon: Sparkles },
  { to: "/app/planner", key: "nav.planner", icon: CalendarDays },
  { to: "/app/gifts", key: "nav.gifts", icon: Gift },
  { to: "/app/budget", key: "nav.budget", icon: Wallet },
  { to: "/app/secret-santa", key: "nav.secretsanta", icon: Users },
  { to: "/app/moment", key: "nav.moment", icon: HandHeart },
  { to: "/app/world", key: "nav.world", icon: Globe },
  { to: "/app/account", key: "nav.account", icon: User },
];

const MOBILE_PRIMARY = [NAV[0], NAV[1], NAV[2], NAV[6], NAV[8]];

export default function AppShell() {
  const { t, lang } = useI18n();
  const { user, logout, setUser } = useAuth();
  // Locale sync on load lives in ProtectedRoute (covers /app, /app/onboarding, /app/create).
  // Language selector: changes ONLY the interface language — the buyer's market (country/currency) is persistent.
  useEffect(() => { setMoneyLocale(lang); }, [lang]);
  const changeLanguage = (l) => {
    if (!user || !LANG_MARKET[l]) return;
    api.patch("/profile", { language: l }).then(({ data }) => setUser(data)).catch(() => {});
  };
  const navigate = useNavigate();
  const isAdminArea = useLocation().pathname.startsWith("/admin");
  const [drawer, setDrawer] = useState(false);

  const navItems = user?.role === "admin"
    ? [...NAV, { to: "/admin", key: "nav.admin", icon: Shield }]
    : NAV;

  const linkClass = ({ isActive }) =>
    `group flex items-center gap-3 rounded-xl px-4 py-3 text-sm transition-all ${
      isActive
        ? "bg-xmas-gold/15 text-xmas-gold border border-xmas-gold/40"
        : "text-xmas-slate hover:bg-white/5 hover:text-xmas-snow border border-transparent"
    }`;

  const doLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  if (isAdminArea) return <Outlet />;

  return (
    <div className="min-h-screen bg-xmas-navy text-xmas-snow">
      {/* Desktop sidebar */}
      <aside className="fixed left-0 top-0 z-40 hidden h-screen w-64 flex-col border-r border-xmas-gold/15 bg-xmas-navycard/80 backdrop-blur-xl lg:flex">
        <Link to="/app" className="flex items-center gap-2 px-6 py-6" data-testid="shell-brand-logo">
          <TreePine className="h-6 w-6 text-xmas-gold" />
          <span className="font-display text-lg font-bold tracking-wider text-xmas-snow">MY CHRISTMAS</span>
        </Link>
        <nav className="flex-1 space-y-1 overflow-y-auto px-3 pb-4">
          {navItems.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={linkClass} data-testid={`sidebar-${n.key.split(".")[1]}`}>
              <n.icon className="h-5 w-5 shrink-0" />
              <span>{t(n.key)}</span>
            </NavLink>
          ))}
        </nav>
        <button
          onClick={doLogout}
          data-testid="sidebar-logout"
          className="mx-3 mb-6 flex items-center gap-3 rounded-xl px-4 py-3 text-sm text-xmas-slate transition hover:bg-xmas-red/20 hover:text-xmas-snow"
        >
          <LogOut className="h-5 w-5" /> {t("cta.logout")}
        </button>
      </aside>

      {/* Top bar */}
      <header className="fixed left-0 right-0 top-0 z-30 flex h-16 items-center justify-between border-b border-xmas-gold/15 bg-xmas-navy/85 px-4 backdrop-blur-xl lg:left-64">
        <button
          className="lg:hidden"
          onClick={() => setDrawer(true)}
          aria-label="Open menu"
          data-testid="mobile-menu-button"
        >
          <Menu className="h-6 w-6 text-xmas-gold" />
        </button>
        <Link to="/app" className="flex items-center gap-2 lg:hidden" data-testid="shell-brand-logo-mobile">
          <TreePine className="h-5 w-5 text-xmas-gold" />
          <span className="font-display text-sm font-bold tracking-wider">MY CHRISTMAS</span>
        </Link>
        <div className="flex items-center gap-3">
          <LanguageSelector compact onChange={changeLanguage} />
          <div className="hidden items-center gap-2 sm:flex">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-8 w-8 rounded-full border border-xmas-gold/40" />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-xmas-gold/20 text-sm font-semibold text-xmas-gold">
                {(user?.name || "?").charAt(0).toUpperCase()}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {drawer && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDrawer(false)} />
          <div className="absolute left-0 top-0 h-full w-72 overflow-y-auto bg-xmas-navycard p-4 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <span className="font-display font-bold tracking-wider text-xmas-gold">MY CHRISTMAS</span>
              <button onClick={() => setDrawer(false)} aria-label="Close menu"><X className="h-6 w-6 text-xmas-snow" /></button>
            </div>
            <nav className="space-y-1">
              {navItems.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.end} className={linkClass} onClick={() => setDrawer(false)}>
                  <n.icon className="h-5 w-5" /> <span>{t(n.key)}</span>
                </NavLink>
              ))}
              <button onClick={doLogout} className="mt-2 flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm text-xmas-slate hover:bg-xmas-red/20 hover:text-xmas-snow">
                <LogOut className="h-5 w-5" /> {t("cta.logout")}
              </button>
            </nav>
          </div>
        </div>
      )}

      {/* Content */}
      <main className="relative min-h-screen pb-24 pt-16 lg:pl-64 lg:pb-8">
        <SnowLayer count={18} />
        <div className="relative z-10 mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8">
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-30 flex items-center justify-around border-t border-xmas-gold/15 bg-xmas-navycard/95 px-1 py-2 backdrop-blur-xl lg:hidden">
        {MOBILE_PRIMARY.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={`bottomnav-${n.key.split(".")[1]}`}
            className={({ isActive }) =>
              `flex flex-1 flex-col items-center gap-1 rounded-lg py-1.5 text-[10px] transition ${
                isActive ? "text-xmas-gold" : "text-xmas-slate"
              }`
            }
          >
            <n.icon className="h-5 w-5" />
            <span className="truncate">{t(n.key)}</span>
          </NavLink>
        ))}
      </nav>
      <AmbientMusic />
      <TutorialModal />
    </div>
  );
}
